import { useState } from 'react'
import {
  gravatarUrlFromHash,
  useGravatarUrl,
  initialsFromDisplayName,
} from '../lib/gravatar.js'

// Round avatar with Gravatar lookup + initials fallback. Same visual we use
// across Workbench / Badge / Jyo so the four apps feel consistent.
//
// Sizes are tailwind classes (h-X w-X) so callers control the bounding box;
// `gravatarSize` controls the requested image resolution (2x for retina).
//
// Pass `gravatarHash` whenever the server already computed it (post-#117 the
// public events DTO does). The `email` prop is kept as a transitional
// fallback for surfaces still wired to the old shape.
export default function Avatar({
  gravatarHash,
  email,
  displayName,
  size = 'h-8 w-8',
  textSize = 'text-xs',
  className = '',
}) {
  // Prefer the precomputed hash — that path is synchronous and never leaks
  // the email through this component.
  const fromHash = gravatarUrlFromHash(gravatarHash, 64)
  const fromEmail = useGravatarUrl(fromHash ? null : email, 64)
  const url = fromHash || fromEmail
  const [imgFailed, setImgFailed] = useState(false)
  const showImage = url && !imgFailed
  const initials = initialsFromDisplayName(displayName, email)

  return (
    <span
      aria-hidden={!displayName && !email && !gravatarHash}
      title={displayName || email || undefined}
      className={[
        'inline-flex items-center justify-center overflow-hidden rounded-full',
        'bg-brand-100 text-brand-800 font-semibold ring-1 ring-white shadow-sm',
        size, textSize, className,
      ].join(' ')}
    >
      {showImage ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setImgFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span>{initials}</span>
      )}
    </span>
  )
}
